import React, {useRef, useEffect, useState, useCallback} from 'react'
import {
    Box,
    IconButton,

    /* Hooks */
    useMultiStyleConfig,
    useBreakpointValue
} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/* Utils */
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../utils/contentRenderer'
import {getHideStyles} from '../../utils/utils'
import {loadOnInteractionOrIdle} from '../../../../utils/load-on-interaction'

/* Components */
import HeroSliderCustomNavigation from '../../layouts/heroPanelBlock/partials/heroSliderCustomNavigation'

/* Styling */
import './styles/swiper-pagination.css'

/* Icons */
import {Play, Pause} from '../../../icons/index'

const HeroPanelBlock = ({PanelItems, HideOnDevices, ...props}) => {
    const styles = useMultiStyleConfig('HeroPanelBlock', {...props})
    const global = useMultiStyleConfig('GlobalCMS', {...props})
    const isMobile = useBreakpointValue({base: true, md: false})
    const swiperRef = useRef(null)
    const [sliderTitles, setSliderTitles] = useState([])
    const [sliderColors, setSliderColors] = useState([])
    const [nextTitle, setNextTitle] = useState(null)
    const [currentTitleColor, setCurrentTitleColor] = useState(null)
    const [isRunning, setIsRunning] = useState(null)
    const [noAutoplay, setNoAutoplay] = useState(true)
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
    const [isTransitioning, setIsTransitioning] = useState(false)

    // Progressive hydration state
    const [swiperLoaded, setSwiperLoaded] = useState(false)
    const [SwiperComponents, setSwiperComponents] = useState(null)

    const onAutoplayTimeLeft = (swiper, time, progress) => {
        // Only update progress if autoplay is actually running
        if (!isRunning) return

        const activeIndex = swiper.realIndex
        const bullets = document.querySelectorAll(
            '.custom-swiper-pagination .swiper-pagination-bullet'
        )
        const line = document.querySelector(
            '.custom-swiper-pagination .active-navigation .custom-pagination-line'
        )
        const progressPercent = (1 - progress) * 100

        if (bullets[activeIndex]) {
            const afterElement = bullets[activeIndex]

            afterElement.style.setProperty('--progress', `${progressPercent}%`)
        }

        line?.style?.setProperty('--progress', `${progressPercent}%`)
    }

    const onSlideChangeStart = (swiper) => {
        // Start fade out effect
        setIsTransitioning(true)
    }

    const onSlideChange = useCallback(
        (swiper) => {
            const bullets = document.querySelectorAll(
                '.custom-swiper-pagination .swiper-pagination-bullet'
            )
            const line = document.querySelector(
                '.custom-swiper-pagination .active-navigation .custom-pagination-line'
            )

            bullets.forEach((bullet, index) => {
                if (index < swiper.realIndex) {
                    bullet.classList.add('completed')
                } else {
                    bullet.classList.remove('completed')
                    bullet.style.removeProperty('--progress')
                }
            })

            // Reset progress line for new slide
            line?.style?.setProperty('--progress', '0%')

            const nextSlideIndex =
                sliderTitles.length > swiper?.realIndex + 1 ? swiper?.realIndex + 1 : 0
            setNextTitle(sliderTitles[nextSlideIndex])
            setCurrentTitleColor(sliderColors[swiper?.realIndex])

            // Update current slide index
            setCurrentSlideIndex(swiper.realIndex)

            // Pause videos inside swiper slides when slide changes (prevents iOS fullscreen issues)
            // Only target videos inside swiper-slide, not the hero video outside
            const slideVideos = document.querySelectorAll('.swiper-slide video')
            if (slideVideos.length > 0) {
                slideVideos.forEach((video) => {
                    video.pause()
                })
            }

            // End fade in effect after a short delay to allow fade out to complete
            setTimeout(() => {
                setIsTransitioning(false)
            }, 400)
        },
        [sliderTitles, sliderColors]
    )

    const onAfterInit = (swiper) => {
        setIsRunning(swiper?.autoplay?.running)
        setNoAutoplay(!swiper?.autoplay?.running && !swiper?.autoplay?.paused)
        setCurrentSlideIndex(swiper.realIndex)
    }

    const onPrevSlide = () => {
        const swiper = swiperRef.current.swiper
        // Resume first if autoplay is paused — calling stop() while paused puts Swiper in an invalid state
        swiper.autoplay.paused && swiper.autoplay.resume()
        swiper.autoplay.stop()
        setIsRunning(false)
        swiper.slidePrev()
    }

    const onNextSlide = () => {
        const swiper = swiperRef.current.swiper
        // Resume first if autoplay is paused — calling stop() while paused puts Swiper in an invalid state
        swiper.autoplay.paused && swiper.autoplay.resume()
        swiper.autoplay.stop()
        setIsRunning(false)
        swiper.slideNext()
    }

    const handlePause = () => {
        swiperRef.current.swiper.autoplay.pause()
        setIsRunning(false)
    }

    const handlePlay = () => {
        const swiper = swiperRef.current.swiper
        swiper.autoplay[swiper.autoplay.paused ? 'resume' : 'start']()
        setIsRunning(true)
    }

    const pagination = {
        clickable: true,
        renderBullet: function (index, className) {
            return `<span class="${className}"></span>`
        }
    }

    const onTouchStart = () => {
        if (swiperRef.current?.swiper) {
            const swiper = swiperRef.current.swiper
            // Resume first if autoplay is paused — calling stop() while paused puts Swiper in an invalid state
            swiper.autoplay.paused && swiper.autoplay.resume()
            swiper.autoplay.stop()
            setIsRunning(false)
        }
    }

    // Initialize slider metadata
    useEffect(() => {
        setSliderTitles(
            PanelItems?.map((slide) => {
                return slide?.Expanded?.Title || ''
            }) || []
        )

        setSliderColors(
            PanelItems?.map((slide) => {
                return slide?.Expanded?.PanelCtaBlockTitleStyle?.TextColorDesktop || 'black'
            }) || []
        )
    }, [PanelItems])

    useEffect(() => {
        setNextTitle(sliderTitles.length > 0 ? sliderTitles[1] || sliderTitles[0] : null)
    }, [sliderTitles])

    useEffect(() => {
        setCurrentTitleColor(sliderColors.length > 0 ? sliderColors[0] : null)
    }, [sliderColors])

    // Progressive hydration: Load Swiper on interaction or idle (only if multiple slides)
    useEffect(() => {
        // Skip on server or if only one slide
        if (typeof window === 'undefined' || !PanelItems || PanelItems.length <= 1) {
            return
        }

        const loadSwiper = async () => {
            try {
                // Dynamic import Swiper and its modules
                const [swiperReact, swiperModules] = await Promise.all([
                    import('swiper/react'),
                    import('swiper/modules')
                ])

                setSwiperComponents({
                    Swiper: swiperReact.Swiper,
                    SwiperSlide: swiperReact.SwiperSlide,
                    modules: [
                        swiperModules.Autoplay,
                        swiperModules.Parallax,
                        swiperModules.Pagination,
                        swiperModules.Navigation
                    ]
                })
                setSwiperLoaded(true)
            } catch (error) {
                console.error('Failed to load Swiper:', error)
            }
        }

        // Add dynamic styles for animations
        const style = document.createElement('style')
        style.id = 'hero-panel-styles'
        style.textContent = `
            .custom-swiper-pagination .swiper-pagination-bullet::after {
                width: var(--progress, 0%);
                transition: width 0.1s linear;
            }
            .custom-swiper-pagination .active-navigation .custom-pagination-line::after {
                width: var(--progress, 0%);
                transition: width 0.1s linear;
            }
            .slide-content {
                transition: opacity 1s ease-in-out;
            }
            .slide-content.fade-out {
                opacity: 0;
            }
            .slide-content.fade-in {
                opacity: 1;
            }
        `

        if (!document.getElementById('hero-panel-styles')) {
            document.head.appendChild(style)
        }

        // Defer Swiper loading until user interaction or 2s idle
        const cleanup = loadOnInteractionOrIdle(loadSwiper, {timeout: 2000})

        return () => {
            cleanup?.()
            const existingStyle = document.getElementById('hero-panel-styles')
            if (existingStyle) {
                existingStyle.remove()
            }
        }
    }, [PanelItems])

    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return null

    const firstSlide = PanelItems?.[0]
    const hasMultipleSlides = PanelItems?.length > 1

    // Static first slide render (SSR + before Swiper loads)
    const renderStaticFirstSlide = () => (
        <Box position="relative" width="100%" height="100%">
            {/* Desktop: Image only */}
            <Box display={{base: 'none', lg: 'block'}}>
                {renderComponentsSwitch(firstSlide?.Expanded?.ResponsiveBackgroundMedia?.Expanded, {
                    isHero: true,
                    fetchPriority: 'high'
                })}
            </Box>
            {/* Mobile: Image + Content */}
            <Box display={{base: 'block', lg: 'none'}}>
                {renderComponentsSwitch(firstSlide?.Expanded?.ResponsiveBackgroundMedia?.Expanded, {
                    isHero: true,
                    fetchPriority: 'high'
                })}
                {renderLayoutsSwitch(firstSlide?.Expanded, {
                    pcbVariant: 'TextOnly'
                })}
            </Box>
            {/* Desktop: Content overlay */}
            {firstSlide?.Expanded?.Gradient && <Box {...global.overlay} zIndex={1}></Box>}
            <Box
                display={{base: 'none', lg: 'block'}}
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={2}
                pointerEvents="none"
            >
                <Box style={{pointerEvents: 'auto'}}>
                    {renderLayoutsSwitch(firstSlide?.Expanded, {
                        pcbVariant: 'TextOnly'
                    })}
                </Box>
            </Box>
            {/* Pagination placeholder for multiple slides (prevents CLS) */}
            {hasMultipleSlides && (
                <>
                    {/* Mobile pagination dots placeholder */}
                    <Box
                        display={{base: 'flex', lg: 'none'}}
                        position="absolute"
                        bottom="20px"
                        left="50%"
                        transform="translateX(-50%)"
                        gap={2}
                        zIndex={3}
                    >
                        {PanelItems.map((_, idx) => (
                            <Box
                                key={idx}
                                width="8px"
                                height="8px"
                                borderRadius="50%"
                                bg={idx === 0 ? 'white' : 'whiteAlpha.500'}
                            />
                        ))}
                    </Box>
                    {/* Desktop navigation placeholder */}
                    <Box display={{base: 'none', lg: 'block'}}>
                        <HeroSliderCustomNavigation
                            nextTitle={sliderTitles[1] || sliderTitles[0]}
                            handleNextSlide={() => {}} // Will be replaced when Swiper loads
                            handlePrevSlide={() => {}}
                            noAutoplay={true}
                            currentTitleColor={sliderColors[0]}
                            fadeClass="fade-in"
                        />
                    </Box>
                </>
            )}
        </Box>
    )

    // Full Swiper carousel render (after Swiper loads)
    const renderSwiperCarousel = () => {
        if (!SwiperComponents) return null

        const {Swiper, SwiperSlide, modules} = SwiperComponents

        return (
            <>
                <Swiper
                    ref={swiperRef}
                    slidesPerView={1}
                    speed={1000}
                    allowTouchMove={isMobile}
                    autoplay={{
                        delay: 7000,
                        disableOnInteraction: false
                    }}
                    loop={true}
                    parallax={true}
                    pagination={isMobile && pagination && !noAutoplay}
                    navigation={isMobile}
                    modules={modules}
                    onAutoplayTimeLeft={onAutoplayTimeLeft}
                    onSlideChangeTransitionStart={onSlideChangeStart}
                    onSlideChange={onSlideChange}
                    onAfterInit={onAfterInit}
                    onTouchStart={onTouchStart}
                >
                    {/* Render all slides */}
                    {PanelItems?.map((PanelCtaBlock, index) => {
                        return (
                            <SwiperSlide key={index}>
                                <Box display={{base: 'none', lg: 'block'}}>
                                    {renderComponentsSwitch(
                                        PanelCtaBlock?.Expanded?.ResponsiveBackgroundMedia
                                            ?.Expanded,
                                        {
                                            isHero: index === 0,
                                            fetchPriority: index === 0 ? 'high' : undefined
                                        }
                                    )}
                                </Box>
                                <Box display={{base: 'block', lg: 'none'}}>
                                    {renderComponentsSwitch(
                                        PanelCtaBlock?.Expanded?.ResponsiveBackgroundMedia
                                            ?.Expanded,
                                        {
                                            isHero: index === 0,
                                            fetchPriority: index === 0 ? 'high' : undefined
                                        }
                                    )}
                                    {renderLayoutsSwitch(PanelCtaBlock?.Expanded, {
                                        pcbVariant: 'TextOnly'
                                    })}
                                </Box>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>

                {/* Desktop Only: Render only the content for the current slide with fade effect */}
                <Box display={{base: 'none', lg: 'block'}}>
                    {PanelItems?.map((PanelCtaBlock, index) => {
                        const isCurrentSlide = index === currentSlideIndex
                        const shouldShow = isCurrentSlide
                        const fadeClass = isTransitioning ? 'fade-out' : 'fade-in'

                        return (
                            <React.Fragment key={`content-${index}`}>
                                {PanelCtaBlock?.Expanded?.Gradient && (
                                    <Box {...global.overlay} zIndex={1}></Box>
                                )}
                                <Box
                                    className={`slide-content ${fadeClass}`}
                                    style={{
                                        display: shouldShow ? 'block' : 'none',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 2,
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <Box style={{pointerEvents: 'auto'}}>
                                        {renderLayoutsSwitch(PanelCtaBlock?.Expanded, {
                                            pcbVariant: 'TextOnly'
                                        })}
                                    </Box>
                                </Box>
                            </React.Fragment>
                        )
                    })}
                </Box>

                {/* Desktop Only: Custom Navigation */}
                <Box display={{base: 'none', lg: 'block'}}>
                    {PanelItems?.map((PanelCtaBlock, index) => {
                        const isCurrentSlide = index === currentSlideIndex
                        const shouldShow = isCurrentSlide
                        const fadeClass = isTransitioning ? 'fade-out' : 'fade-in'

                        return (
                            <Box
                                key={`swiper-custom-navigation-${index}`}
                                className={shouldShow ? 'active-navigation' : ''}
                                style={{
                                    display: shouldShow ? 'block' : 'none'
                                }}
                            >
                                <HeroSliderCustomNavigation
                                    nextTitle={nextTitle}
                                    handleNextSlide={onNextSlide}
                                    handlePrevSlide={onPrevSlide}
                                    noAutoplay={noAutoplay}
                                    currentTitleColor={currentTitleColor}
                                    fadeClass={fadeClass}
                                />
                            </Box>
                        )
                    })}
                </Box>

                {/* Play/Pause Controls */}
                <Box {...styles.controls}>
                    {!noAutoplay &&
                        (isRunning ? (
                            <IconButton
                                variant="unstyled"
                                h={'auto'}
                                minW={'auto'}
                                onClick={handlePause}
                                icon={<Pause {...styles.control} />}
                                aria-label="Pause"
                            />
                        ) : (
                            <IconButton
                                variant="unstyled"
                                h={'auto'}
                                minW={'auto'}
                                onClick={handlePlay}
                                icon={<Play {...styles.control} />}
                                aria-label="Play"
                            />
                        ))}
                </Box>
            </>
        )
    }

    return (
        <Box {...styles.container} {...hideStyles} data-block-type="HeroPanelBlock">
            <Box
                {...global.HeroSize}
                {...styles.wrapper}
                className={'custom-swiper-pagination'}
                sx={{
                    // Target PanelCtaBlock only when it contains ResponsiveMediaBlock (default variant, not TextOnly)
                    '& [data-block-type="PanelCtaBlock"]:has([data-block-type="ResponsiveMediaBlock"])': {
                        width: '100%',
                        height: '100%'
                    },
                    // Target ResponsiveMediaBlock and its children
                    '& [data-block-type="ResponsiveMediaBlock"], [data-block-type="ResponsiveMediaBlock"] div, [data-block-type="ResponsiveMediaBlock"] picture': {
                        width: '100%',
                        height: '100%'
                    },
                    '& img': {
                        width: '100% !important',
                        height: '100% !important',
                        objectFit: 'cover !important'
                    }
                }}
            >
                {hasMultipleSlides
                    ? // Multiple slides: show static first, then Swiper when loaded
                      swiperLoaded && SwiperComponents
                        ? renderSwiperCarousel()
                        : renderStaticFirstSlide()
                    : // Single slide: just render the content directly
                      PanelItems?.map((PanelCtaBlock, index) => {
                          return (
                              <React.Fragment key={index}>
                                  {renderLayoutsSwitch(PanelCtaBlock?.Expanded)}
                              </React.Fragment>
                          )
                      })}
            </Box>
        </Box>
    )
}

HeroPanelBlock.propTypes = {
    PanelItems: PropTypes.array,
    HideOnDevices: PropTypes.array
}

export default HeroPanelBlock
